import { baseApi } from '@/store/api/baseApi';
import { ApiTags, AuditTagIds } from '@/store/api/tags';

import { Side } from '@/utils/enums';

type AuditItemShared = {
  auditEventType: string;
  auditEventDatetime: string;
  auditEventUser: string;
  description: string;
  createdAt: string;
  modifiedAt: string;
  id: number;
  createdBy: string;
  updatedBy: string;
  fixedPrice: number;
  tenorMonths: number;
};

type PositionTags = {
  matching247: boolean;
  basisSharing: boolean;
  bessOption: boolean;
  capacityRights: boolean;
  colocationOption: boolean;
  escalator: boolean;
  feocCompliant: boolean;
  financingPartyConsentRequired: boolean;
  fullDeliverability: boolean;
  itcSafeHarbored: boolean;
  nodalSettlement: boolean;
  priceCap: boolean;
  priceCollar: boolean;
  priceFloor: boolean;
  realTimeDataAccess: boolean;
  ssa: boolean;
  suretyBond: boolean;
  taxCreditTransferability: boolean;
  upfrontPayment: boolean;
  upsideRevenueShare: boolean;
};

export type AuditItemBid = AuditItemShared &
  PositionTags & {
    companyId: number;
    ticker: string;
    contractLegalEntity: string;
    status: string;
    productType: string;
    technology: string;
    contractType: string;
    iso: string;
    requestedVolume: number;
    hubZoneMatrixId: string;
    settlementPoint: string;
    settlementIndex: string;
    fixedPriceMin: number;
    fixedPriceMax: number;
    tenorStart: string;
    internalReference: string;
    targetCod: string;
    guaranteedCod: string;
    countryId: number;
    stateId: number;
    cityName: string;
    negativePricingMethod: string;
    performanceGuaranteeMethod: string;
    applicableRecProgram: string;
    applicableTrackingSystem: string;
    recDeliveryMethodology: string;
    creditProfile: string;
    buyerPostCodCreditSupport: number;
    creditDescription: string;
    expectedSettlementMin: number;
    expectedSettlementMax: number;
    downsideRiskMin: number;
    downsideRiskMax: number;
    historicalNodalLmeMin: number;
    historicalNodalLmeMax: number;
    impliedCarbonPriceMin: number;
    impliedCarbonPriceMax: number;
    carbonFreeEnergyMin: number;
    carbonFreeEnergyMax: number;
  };

export type AuditItemOffer = AuditItemShared &
  PositionTags & {
    ticker: string;
    projectId: string;
    productType: string;
    contractType: string;
    contractLegalEntity: string;
    iso: string;
    internalReference: string;
    hubZoneMatrixId: number;
    settlementPoint: string;
    settlementPointIndex: string;
    tenorStartDate: string;
    availableCapacityAc: number;
    buyerFraction: number;
    creditProfile: string;
    creditProfileDescription: string;
    sellerPostCodCollateral: number;
    negativePricingMethod: string;
    applicableRecProgram: string;
    applicableTrackingSystem: string;
    recDeliveryMethodology: string;
    performanceGuaranteeMethod: string;
    status: string;
    companyId: string;
    resuretyExecutionId: number | null;
  };

export type AuditItemNegotiation = AuditItemShared & {
  projectId: string;
  buyerCompanyId: string;
  sellerCompanyId: string;
  buyerCompanyName: string;
  sellerCompanyName: string;
  offerId: number;
  bidId: number;
  offeredCapacity: number;
  tenorStart: string;
  sellerPostCodCollateral: number;
  buyerPostCodCollateral: number;
  proposalSender: string;
  proposalCount: number;
  negotiationStatus: string;
  isExclusive: boolean;
  requestUuid: string;
  projectUuid: string;
  buyerUserConfirmed: string;
  sellerUserConfirmed: string;
  buyerExecutedAt: string;
  sellerExecutedAt: string;
  originator: string;
  uniqueTransactionIdentifier: string;
  upi: string;
  submissionId: string;
  reportingCounterparty: Side;
  monthEntered: string;
  yearEntered: string;
  executedDateTime: string;
  version: number;
};

const BASE_URL = '/api/v1/audit';

export const auditApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getBidAuditTrail: build.query<AuditItemBid[], string>({
      query: bidId => ({
        url: `${BASE_URL}/bids/${bidId}`,
      }),
      providesTags: (result, error, bidId) => [
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.BID}-${bidId}`,
        },
      ],
    }),
    getOfferAuditTrail: build.query<AuditItemOffer[], string>({
      query: offerId => ({
        url: `${BASE_URL}/offers/${offerId}`,
      }),
      providesTags: (result, error, offerId) => [
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.OFFER}-${offerId}`,
        },
      ],
    }),
    getNegotiationAuditTrail: build.query<AuditItemNegotiation[], string>({
      query: negotiationId => ({
        url: `${BASE_URL}/negotiations/${negotiationId}`,
      }),
      providesTags: (result, error, negotiationId) => [
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.NEGOTIATION}-${negotiationId}`,
        },
      ],
    }),
  }),
});

export const {
  useGetBidAuditTrailQuery,
  useGetOfferAuditTrailQuery,
  useGetNegotiationAuditTrailQuery,
} = auditApi;
